import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ReservationPaxGroup,
    ServiceBooking,
    Person,
    ServiceBookingPaxGroup,
} from '@turizi/shared';
import {
    Button,
    TextInput,
    ActionIcon,
    Select,
    Tooltip,
} from '@mantine/core';
import {
    IconUsers,
    IconUsersGroup,
    IconSearch,
    IconX,
    IconCheck,
    IconTrash,
    IconArrowsSplit2,
    IconSparkles,
} from '@tabler/icons-react';
import CardModal from '../card-modal';
import {
    useReservationPaxGroups,
    useBulkAssignPaxGroupsToBookings,
} from '@/hooks/api-hooks';
import { BookingPaxsSize, ShowPaxGroup } from '../snippets';
import { ServiceTitleStatic } from '../services/service-title';
import { formatDayShort, sortPersons } from '@/lib/format-utils';
import { resolveBookingPaxGroupPersons } from '@/lib/models-utils';
import { notify } from '@/lib/notify';

interface PaxDistributionModalProps {
    opened: boolean;
    onClose: () => void;
    reservationId: number;
    bookings: ServiceBooking[];
    initialGroupId?: number | null;
    preFilteredToCluster?: boolean;
}

interface BookingDraft {
    linked: boolean;
    adults: number;
    children: number;
    seniors: number;
    infants: number;
    guests: number;
    personIds: number[];
}

function getBookingServiceGroupKey(booking: ServiceBooking): string {
    const svcId = booking.service_id ?? booking.service_data?.id ?? 'unknown';
    const startDay = booking.start_day || '';
    const endDay = booking.end_day || '';
    return `${svcId}|${startDay}|${endDay}`;
}

export default function PaxDistributionModal({
    opened,
    onClose,
    reservationId,
    bookings,
    initialGroupId,
    preFilteredToCluster = false,
}: PaxDistributionModalProps) {
    const { t } = useTranslation();
    const { data: paxGroups = [] } = useReservationPaxGroups(reservationId);
    const bulkAssignMutation = useBulkAssignPaxGroupsToBookings();

    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [selectedClusterKey, setSelectedClusterKey] = useState<string>('all');
    const [bookingDrafts, setBookingDrafts] = useState<Record<number, BookingDraft>>({});
    const [activeBookingId, setActiveBookingId] = useState<number | null>(null);
    const [paxSearch, setPaxSearch] = useState('');

    // Filter out package parent rows
    const eligibleBookings = useMemo(() => {
        return (bookings || []).filter(
            (b) => b.id && !(b.service_data?.child_services?.length > 0)
        );
    }, [bookings]);

    // Group bookings by service + dates
    const serviceClusters = useMemo(() => {
        const map = new Map<string, { key: string; label: string; bookings: ServiceBooking[] }>();
        for (const b of eligibleBookings) {
            const key = getBookingServiceGroupKey(b);
            if (!map.has(key)) {
                const title = b.service_data ? ServiceTitleStatic(b.service_data) : `#${b.id}`;
                const dateStr = b.start_day ? ` (${formatDayShort(b.start_day)})` : '';
                map.set(key, {
                    key,
                    label: `${title}${dateStr}`,
                    bookings: [],
                });
            }
            map.get(key)!.bookings.push(b);
        }
        return Array.from(map.values());
    }, [eligibleBookings]);

    const targetBookings = useMemo(() => {
        if (selectedClusterKey === 'all' || preFilteredToCluster) {
            return eligibleBookings;
        }
        const cluster = serviceClusters.find((c) => c.key === selectedClusterKey);
        return cluster ? cluster.bookings : eligibleBookings;
    }, [eligibleBookings, selectedClusterKey, serviceClusters, preFilteredToCluster]);

    const selectedGroup: ReservationPaxGroup | undefined = useMemo(() => {
        return paxGroups.find((g) => g.id === selectedGroupId);
    }, [paxGroups, selectedGroupId]);

    const groupPersons: Person[] = useMemo(() => {
        if (!selectedGroup?.persons) return [];
        return sortPersons(selectedGroup.persons).filter((p): p is Person & { id: number } => Boolean(p.id));
    }, [selectedGroup]);

    // Initialize selected group when modal opens
    useEffect(() => {
        if (!opened) return;
        setPaxSearch('');

        if (initialGroupId && paxGroups.some((g) => g.id === initialGroupId)) {
            setSelectedGroupId(initialGroupId);
        } else if (paxGroups.length > 0 && !selectedGroupId) {
            const largest = [...paxGroups].sort(
                (a, b) => (b.persons?.length || b.pax_count || 0) - (a.persons?.length || a.pax_count || 0)
            )[0];
            setSelectedGroupId(largest?.id || null);
        }

        // If not pre-filtered to a cluster, default to the largest multi-booking cluster
        if (!preFilteredToCluster && serviceClusters.length > 1) {
            const largestCluster = [...serviceClusters].sort((a, b) => b.bookings.length - a.bookings.length)[0];
            if (largestCluster && largestCluster.bookings.length > 1) {
                setSelectedClusterKey(largestCluster.key);
            } else {
                setSelectedClusterKey('all');
            }
        } else {
            setSelectedClusterKey('all');
        }
    }, [opened, initialGroupId, paxGroups.length]);

    // Initialize booking drafts whenever selectedGroup or eligibleBookings change
    useEffect(() => {
        if (!opened || !selectedGroup?.id) return;

        const nextDrafts: Record<number, BookingDraft> = {};
        for (const b of eligibleBookings) {
            if (!b.id) continue;
            const existingLink: ServiceBookingPaxGroup | undefined = b.pax_groups?.find(
                (bg) => (bg.pax_group_id || bg.pax_group?.id) === selectedGroup.id
            );

            if (existingLink) {
                const resolvedPersons = resolveBookingPaxGroupPersons({
                    ...existingLink,
                    pax_group: existingLink.pax_group || selectedGroup,
                });
                nextDrafts[b.id] = {
                    linked: true,
                    adults: existingLink.adults ?? b.adults ?? 0,
                    children: existingLink.children ?? b.children ?? 0,
                    seniors: existingLink.seniors ?? b.seniors ?? 0,
                    infants: existingLink.infants ?? b.infants ?? 0,
                    guests: existingLink.guests ?? b.guests ?? 0,
                    personIds: resolvedPersons.map((p) => Number(p.id)).filter(Boolean),
                };
            } else {
                nextDrafts[b.id] = {
                    linked: false,
                    adults: b.adults || 0,
                    children: b.children || 0,
                    seniors: b.seniors || 0,
                    infants: b.infants || 0,
                    guests: b.guests || 0,
                    personIds: [],
                };
            }
        }
        setBookingDrafts(nextDrafts);
    }, [opened, selectedGroup?.id, eligibleBookings]);

    // Default activeBookingId when targetBookings change
    useEffect(() => {
        if (targetBookings.length > 0) {
            if (!activeBookingId || !targetBookings.some((b) => b.id === activeBookingId)) {
                setActiveBookingId(targetBookings[0].id || null);
            }
        }
    }, [targetBookings]);

    // Map personId -> list of booking IDs in targetBookings where they are assigned
    const personAssignmentMap = useMemo(() => {
        const map = new Map<number, number[]>();
        targetBookings.forEach((b) => {
            if (!b.id) return;
            const draft = bookingDrafts[b.id];
            if (!draft || !draft.linked) return;
            for (const pid of draft.personIds) {
                if (!map.has(pid)) map.set(pid, []);
                map.get(pid)!.push(b.id);
            }
        });
        return map;
    }, [targetBookings, bookingDrafts]);

    const assignedPersonsCount = useMemo(() => {
        let count = 0;
        for (const p of groupPersons) {
            if (p.id && (personAssignmentMap.get(Number(p.id))?.length || 0) > 0) {
                count++;
            }
        }
        return count;
    }, [groupPersons, personAssignmentMap]);

    const unassignedPersonsCount = Math.max(0, groupPersons.length - assignedPersonsCount);

    const filteredPersons = useMemo(() => {
        const q = paxSearch.trim().toLowerCase();
        if (!q) return groupPersons;
        return groupPersons.filter((p) =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.document_number || '').toLowerCase().includes(q)
        );
    }, [groupPersons, paxSearch]);

    // Auto-distribute sequentially according to each booking's capacity
    const handleAutoDistributeByCapacity = () => {
        if (!selectedGroup) return;
        const pIds = groupPersons.map((p) => Number(p.id)).filter(Boolean);
        let cursor = 0;

        setBookingDrafts((prev) => {
            const next = { ...prev };
            for (const b of targetBookings) {
                if (!b.id) continue;
                const cap = Math.max(1, BookingPaxsSize(b));
                const slice = pIds.slice(cursor, cursor + cap);
                cursor += slice.length;

                next[b.id] = {
                    linked: true,
                    adults: b.adults || 0,
                    children: b.children || 0,
                    seniors: b.seniors || 0,
                    infants: b.infants || 0,
                    guests: b.guests || 0,
                    personIds: slice,
                };
            }
            return next;
        });
    };

    // Clear passenger assignments on target bookings
    const handleClearAssignments = () => {
        setBookingDrafts((prev) => {
            const next = { ...prev };
            for (const b of targetBookings) {
                if (!b.id || !next[b.id]) continue;
                next[b.id] = {
                    ...next[b.id],
                    personIds: [],
                };
            }
            return next;
        });
    };

    // Click a passenger in the left pool to assign/remove from the active booking
    const handleClickPersonInPool = (person: Person) => {
        if (!person.id || !activeBookingId) return;
        const pid = Number(person.id);
        const targetBooking = targetBookings.find((b) => b.id === activeBookingId);
        if (!targetBooking) return;

        const currentDraft = bookingDrafts[activeBookingId] || {
            linked: true,
            adults: targetBooking.adults || 0,
            children: targetBooking.children || 0,
            seniors: targetBooking.seniors || 0,
            infants: targetBooking.infants || 0,
            guests: targetBooking.guests || 0,
            personIds: [],
        };

        const alreadyInActive = currentDraft.personIds.includes(pid);
        let nextPersonIds: number[];

        if (alreadyInActive) {
            nextPersonIds = currentDraft.personIds.filter((id) => id !== pid);
        } else {
            nextPersonIds = [...currentDraft.personIds, pid];
        }

        const updatedDraft: BookingDraft = {
            ...currentDraft,
            linked: true,
            personIds: nextPersonIds,
        };

        setBookingDrafts((prev) => ({
            ...prev,
            [activeBookingId]: updatedDraft,
        }));

        // If we just added a person and the active booking reached its capacity, auto-advance to next non-full booking!
        const bookingCap = Math.max(1, BookingPaxsSize(targetBooking));
        if (!alreadyInActive && nextPersonIds.length >= bookingCap) {
            const currentIdx = targetBookings.findIndex((b) => b.id === activeBookingId);
            for (let offset = 1; offset < targetBookings.length; offset++) {
                const candidate = targetBookings[(currentIdx + offset) % targetBookings.length];
                if (!candidate.id) continue;
                const candDraft = candidate.id === activeBookingId ? updatedDraft : bookingDrafts[candidate.id];
                const candCap = Math.max(1, BookingPaxsSize(candidate));
                if (!candDraft || (candDraft.personIds?.length || 0) < candCap) {
                    setActiveBookingId(candidate.id);
                    break;
                }
            }
        }
    };

    const handleRemovePersonFromBooking = (bookingId: number, personId: number) => {
        setBookingDrafts((prev) => {
            const cur = prev[bookingId];
            if (!cur) return prev;
            return {
                ...prev,
                [bookingId]: {
                    ...cur,
                    personIds: cur.personIds.filter((id) => id !== personId),
                },
            };
        });
    };

    const handleSaveDistribution = async () => {
        if (!selectedGroup?.id) return;

        const hasOrders = targetBookings.some((b) => (b.service_order_bookings?.length || 0) > 0);
        if (hasOrders && !window.confirm(t('confirm_modify_booking_with_service_order'))) {
            return;
        }

        const assignments = targetBookings
            .filter((b): b is ServiceBooking & { id: number } => Boolean(b.id))
            .map((b) => {
                const draft = bookingDrafts[b.id];
                if (!draft || !draft.linked) {
                    return {
                        service_booking_id: b.id,
                        pax_group_id: selectedGroup.id!,
                        unassign: true,
                    };
                }
                return {
                    service_booking_id: b.id,
                    pax_group_id: selectedGroup.id!,
                    adults: draft.adults,
                    children: draft.children,
                    seniors: draft.seniors,
                    infants: draft.infants,
                    guests: draft.guests,
                    is_custom_config: true,
                    person_ids: draft.personIds,
                };
            });

        try {
            await bulkAssignMutation.mutateAsync({
                reservationId,
                assignments,
            });
            notify.success(t('success'), t('pax_distribution_saved'));
            onClose();
        } catch (err) {
            console.error('Error saving pax distribution:', err);
            notify.error(t('error'), t('error'));
        }
    };

    const personById = useMemo(() => {
        const map = new Map<number, Person>();
        for (const p of groupPersons) {
            if (p.id) map.set(Number(p.id), p);
        }
        return map;
    }, [groupPersons]);

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="92%"
            header={
                <div className="flex items-center justify-between w-full pr-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <IconArrowsSplit2 size={20} className="text-teal-600" />
                        <span className="font-bold text-base text-slate-800">
                            {t('distribute_paxs_across_bookings')}
                        </span>
                    </div>
                </div>
            }
            footer={
                <div className="w-full flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-500 font-medium">
                        {t('target_bookings_count', { count: targetBookings.length })}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            onClick={onClose}
                            disabled={bulkAssignMutation.isPending}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            leftSection={<IconCheck size={14} />}
                            loading={bulkAssignMutation.isPending}
                            disabled={!selectedGroup?.id || targetBookings.length === 0}
                            onClick={handleSaveDistribution}
                        >
                            {t('save_distribution')}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-4">
                {/* Top Control Bar: Selected Group Info, Target Service Cluster & Auto-Distribute */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                        {selectedGroup && (
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shrink-0">
                                <IconUsersGroup size={15} className="text-indigo-600" />
                                <span className="font-bold text-xs text-slate-800">{selectedGroup.name}</span>
                                <ShowPaxGroup
                                    size="xs"
                                    adults={selectedGroup.adults}
                                    children={selectedGroup.children}
                                    seniors={selectedGroup.seniors}
                                    infants={selectedGroup.infants}
                                    guests={selectedGroup.guests}
                                />
                            </div>
                        )}

                        {/* Target Service Cluster Filter (when not pre-filtered) */}
                        {!preFilteredToCluster && serviceClusters.length > 1 && (
                            <Select
                                size="xs"
                                value={selectedClusterKey}
                                onChange={(val) => setSelectedClusterKey(val || 'all')}
                                data={[
                                    {
                                        value: 'all',
                                        label: `${t('all_bookings')} (${eligibleBookings.length})`,
                                    },
                                    ...serviceClusters.map((c) => ({
                                        value: c.key,
                                        label: `${c.label} (x${c.bookings.length})`,
                                    })),
                                ]}
                                className="min-w-[280px] sm:min-w-[380px] flex-1 max-w-xl"
                            />
                        )}
                    </div>

                    {/* Quick Action Buttons */}
                    <div className="flex items-center gap-2 shrink-0">
                        <Tooltip label={t('auto_distribute_by_capacity_tooltip')} withArrow>
                            <Button
                                size="xs"
                                color="teal"
                                variant="filled"
                                leftSection={<IconSparkles size={14} />}
                                onClick={handleAutoDistributeByCapacity}
                                disabled={targetBookings.length === 0}
                            >
                                {t('auto_distribute_by_capacity')}
                            </Button>
                        </Tooltip>

                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            leftSection={<IconTrash size={13} />}
                            onClick={handleClearAssignments}
                        >
                            {t('clear')}
                        </Button>
                    </div>
                </div>

                {/* Main 2-Column Distribution Workspace */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                    {/* Left Column: Passengers Table */}
                    <div className="lg:col-span-4 border border-slate-200 rounded-xl bg-white overflow-hidden flex flex-col">
                        <div className="p-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 shrink-0">
                                <IconUsers size={15} className="text-slate-600" />
                                <span className="text-xs font-bold text-slate-800">
                                    {t('paxs')} ({assignedPersonsCount}/{groupPersons.length})
                                </span>
                            </div>
                            <TextInput
                                size="xs"
                                value={paxSearch}
                                onChange={(e) => setPaxSearch(e.currentTarget.value)}
                                placeholder={t('search_passengers')}
                                leftSection={<IconSearch size={13} className="text-slate-400" />}
                                rightSection={
                                    paxSearch ? (
                                        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setPaxSearch('')}>
                                            <IconX size={11} />
                                        </ActionIcon>
                                    ) : null
                                }
                                className="w-48"
                            />
                        </div>

                        <div className="max-h-[64vh] overflow-y-auto subtle-scrollbar">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur-xs border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
                                    <tr>
                                        <th className="py-1.5 px-3">{t('name')}</th>
                                        <th className="py-1.5 px-3 text-right">{t('booking')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredPersons.length === 0 ? (
                                        <tr>
                                            <td colSpan={2} className="p-6 text-center text-xs text-slate-400">
                                                {t('no_results')}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPersons.map((person) => {
                                            const pid = Number(person.id);
                                            const assignedBookingIds = personAssignmentMap.get(pid) || [];
                                            const isInActiveBooking = Boolean(
                                                activeBookingId && bookingDrafts[activeBookingId]?.personIds.includes(pid)
                                            );
                                            const visibleBookingIds = assignedBookingIds.slice(0, 3);
                                            const extraBookings = Math.max(0, assignedBookingIds.length - visibleBookingIds.length);

                                            return (
                                                <tr
                                                    key={pid}
                                                    onClick={() => handleClickPersonInPool(person)}
                                                    className={`cursor-pointer transition-colors select-none ${isInActiveBooking
                                                        ? 'bg-indigo-50/90 hover:bg-indigo-100/70'
                                                        : assignedBookingIds.length > 0
                                                            ? 'bg-emerald-50/20 hover:bg-slate-50'
                                                            : 'bg-white hover:bg-slate-50'
                                                        }`}
                                                >
                                                    <td className="py-1.5 px-3 font-medium text-slate-800">
                                                        {person.name}
                                                    </td>
                                                    <td className="py-1.5 px-3 text-right whitespace-nowrap">
                                                        {assignedBookingIds.length > 0 ? (
                                                            <Tooltip
                                                                label={assignedBookingIds.map((id) => `#${id}`).join(', ')}
                                                                disabled={extraBookings === 0}
                                                                withArrow
                                                                multiline
                                                                maw={260}
                                                            >
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200/70 font-bold text-[10px] tabular-nums">
                                                                    #{visibleBookingIds.join(', #')}
                                                                    {extraBookings > 0 && <span>+{extraBookings}</span>}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className="text-[10px] text-slate-400">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Right Column: Target Bookings Table */}
                    <div className="lg:col-span-8 border border-slate-200 rounded-xl bg-white overflow-hidden flex flex-col">
                        <div className="max-h-[68vh] overflow-y-auto subtle-scrollbar">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur-xs border-b border-slate-200 text-[10px] font-bold uppercase text-slate-500">
                                    <tr>
                                        <th className="py-2 pl-3 pr-2">{t('service')}</th>
                                        <th className="py-2 pl-3 pr-2">{t('date')}</th>
                                        <th className="py-2 px-2 w-24 text-center">{t('capacity')}</th>
                                        <th className="py-2 pl-2 pr-3">{t('paxs')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {targetBookings.map((booking) => {
                                        if (!booking.id) return null;
                                        const bid = booking.id;
                                        const draft = bookingDrafts[bid] || {
                                            linked: false,
                                            adults: booking.adults || 0,
                                            children: booking.children || 0,
                                            seniors: booking.seniors || 0,
                                            infants: booking.infants || 0,
                                            guests: booking.guests || 0,
                                            personIds: [],
                                        };
                                        const isActive = activeBookingId === bid;
                                        const capacity = BookingPaxsSize(booking);
                                        const assignedCount = draft.personIds.length;
                                        const isFull = capacity > 0 && assignedCount === capacity;
                                        const isOver = capacity > 0 && assignedCount > capacity;

                                        return (
                                            <tr
                                                key={bid}
                                                onClick={() => setActiveBookingId(bid)}
                                                className={`cursor-pointer transition-colors 
                                                    ${isActive
                                                        ? 'bg-blue-100'
                                                        : 'bg-white hover:bg-slate-50/80'
                                                    }`}
                                            >
                                                <td className="py-2 pl-3 pr-2 align-middle">
                                                    <div className="text-slate-800 whitespace-nowrap">
                                                        <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] tabular-nums">
                                                            #{bid}
                                                        </span>
                                                        {ServiceTitleStatic(booking.service_data)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="whitespace-nowrap flex items-center gap-1.5 text-[10px] text-slate-400 mt-0.5">
                                                        {formatDayShort(booking.start_day)}
                                                        {booking.end_day && booking.end_day !== booking.start_day
                                                            ? ` → ${formatDayShort(booking.end_day)}`
                                                            : ''}
                                                    </div>
                                                </td>
                                                <td className="py-2 px-2 align-middle text-center whitespace-nowrap">
                                                    <span
                                                        className={`inline-block px-2 py-0.5 rounded-full font-bold text-[11px] tabular-nums ${isFull
                                                            ? 'bg-teal-50 text-teal-700 border border-teal-200'
                                                            : isOver
                                                                ? 'bg-red-50 text-red-700 border border-red-200'
                                                                : assignedCount > 0
                                                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                                    : 'bg-slate-100 text-slate-500'
                                                            }`}
                                                    >
                                                        {assignedCount} / {capacity}
                                                    </span>
                                                </td>
                                                <td className="py-2 pl-2 pr-3 align-middle">
                                                    <div className="flex flex-wrap items-center gap-1">
                                                        {draft.personIds.length === 0 ? (
                                                            <span className="text-[11px] text-slate-400 italic">
                                                                {isActive
                                                                    ? null
                                                                    : t('no_passengers_assigned')}
                                                            </span>
                                                        ) : (
                                                            draft.personIds.map((pid) => {
                                                                const p = personById.get(pid);
                                                                if (!p) return null;
                                                                return (
                                                                    <span
                                                                        key={pid}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-medium text-slate-800 shadow-2xs"
                                                                    >
                                                                        <span>{p.name}</span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemovePersonFromBooking(bid, pid)}
                                                                            className="text-slate-400 hover:text-red-600 p-0.5 rounded hover:bg-red-50 transition-colors cursor-pointer"
                                                                        >
                                                                            <IconX size={11} />
                                                                        </button>
                                                                    </span>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </CardModal>
    );
}
